'use client';

import { useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import type { StatusSnapshot } from '@/lib/status/model';
import { parseKumaTime } from '@/lib/status/model';
import { fmtDate, fmtDay, incidentParagraphs, incidentTone } from './incidentText';
import { SHEET, SheetHeader, SheetLine } from './Sheet';

const DAYS = 30;

export default function IncidentHistory({ snapshot, now }: { snapshot: StatusSnapshot; now: string }) {
  const [open, setOpen] = useState<Set<number>>(new Set());
  /*
   * Expanding a row changes this block's height, and GridClearing measures
   * blocks with getBoundingClientRect — without a nudge the clearing keeps
   * the old height and grid lines run straight through the revealed text.
   */
  useEffect(() => {
    window.dispatchEvent(new Event('grid-relayout'));
  }, [open]);

  const cutoff = new Date(now).getTime() - DAYS * 86_400_000;
  const items = snapshot.history
    .filter((i) => new Date(parseKumaTime(i.createdDate)).getTime() >= cutoff)
    .sort((a, b) => parseKumaTime(b.createdDate).localeCompare(parseKumaTime(a.createdDate)));
  const toggle = (id: number) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <section aria-labelledby="history-h" data-grid-block data-grid-fill="#ffffff" className={SHEET}>
      <SheetHeader
        id="history-h"
        title={`PAST ${DAYS} DAYS`}
        note={items.length === 0 ? 'NO INCIDENTS' : `${items.length} RESOLVED`}
      />
      {items.length === 0 ? (
        <SheetLine>No incidents in the past {DAYS} days.</SheetLine>
      ) : (
        <div>
          {items.map((i) => {
            const isOpen = open.has(i.id);
            return (
              <div key={i.id} id={`incident-${i.id}`} className="border-b border-line">
                <button
                  type="button"
                  onClick={() => toggle(i.id)}
                  aria-expanded={isOpen}
                  className="flex w-full items-center gap-4 px-5 text-left max-md:py-4 md:h-[calc(var(--grid-cell,72px)-1px)]"
                >
                  <span aria-hidden className="h-[9px] w-[9px] shrink-0 rounded-full" style={{ background: incidentTone(i.style) }} />
                  <span className="font-jetbrains text-[11px] text-dim">{fmtDay(parseKumaTime(i.createdDate))}</span>
                  <span className="min-w-0 flex-1 truncate text-[15px] font-semibold text-ink">{i.title}</span>
                  <span className="rounded-full border border-line-2 px-2 py-[2px] font-jetbrains text-[10.5px] text-muted">Resolved</span>
                  <ChevronDown size={16} className={`shrink-0 text-dim transition-transform ${isOpen ? 'rotate-180' : ''}`} aria-hidden />
                </button>
                {isOpen && (
                  <div className="space-y-2 px-5 pb-5 pl-[46px] text-[14.5px] leading-[1.6] text-muted">
                    {incidentParagraphs(i.content).map((p, k) => <p key={k}>{p}</p>)}
                    {i.lastUpdatedDate && <p className="font-jetbrains text-[11px] text-dim">resolved {fmtDate(parseKumaTime(i.lastUpdatedDate))}</p>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
